// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { useDisclosure } from '@/hooks/use-disclosure';
import {
  findDriftCapableIntegration,
  isDriftCapableNeedsReview,
} from '@/integrations/drift-support';
import { DriftResolutionPopover as SFDCDriftResolutionPopover } from '@/sfdc/components/drift-resolution-popover';
import { Integration } from '@/utilities/integration';
import { Box, Chip, Tooltip, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';

// Narrow shape so any caller with key/name/integrations can pass through —
// SparseLocationWithIntegration on detail pages, full Location on the
// autocomplete helper text. Reads only; never mutated.
interface LocationLike {
  key: string;
  name: string;
  integrations: Integration[];
}

interface Props {
  location: LocationLike | null;
  onResolved: () => void;
  // 'chip' (default): amber MUI Chip — used on PO/RO/FO/Shipment detail
  // pages. 'text': red caption text — used inside the location autocomplete
  // helper row where a chip would feel too heavy.
  variant?: 'chip' | 'text';
}

/**
 * Renders an "Out of sync" warning next to a location reference when the
 * linked integration has pending drift. Clicking opens the per-field drift
 * popover the location detail page uses. Returns null when the location is
 * missing, not linked to a drift-capable integration, or in sync — a quiet
 * indicator that only surfaces when there's something to act on.
 */
export function LocationIntegrationStatus(props: Props) {
  const {
    isOpen: isDriftOpen,
    open: openDrift,
    close: closeDrift,
  } = useDisclosure();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const setAnchorRef = useCallback((node: HTMLElement | null) => {
    setAnchorEl(node);
  }, []);
  // Close the popover if the anchor unmounts (e.g. parent refetched after
  // a Pull and the row reshuffled) or scrolls off-screen.
  useEffect(() => {
    if (anchorEl === null && isDriftOpen) {
      closeDrift();
    }
  }, [anchorEl, isDriftOpen, closeDrift]);
  useEffect(() => {
    if (!anchorEl || !isDriftOpen) {
      return;
    }
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) {
        closeDrift();
      }
    });
    observer.observe(anchorEl);
    return () => observer.disconnect();
  }, [anchorEl, isDriftOpen, closeDrift]);

  if (!props.location) {
    return null;
  }
  const driftIntegration = findDriftCapableIntegration(
    props.location.integrations
  );
  if (!driftIntegration || !isDriftCapableNeedsReview(driftIntegration)) {
    return null;
  }

  const tooltipTitle = `This location is linked to ${driftIntegration.label} but data is out of sync (click to view status)`;
  const variant = props.variant ?? 'chip';

  return (
    <>
      <Tooltip title={tooltipTitle} placement="bottom" arrow>
        {variant === 'text' ? (
          <Box
            ref={setAnchorRef}
            component="span"
            onClick={openDrift}
            role="button"
            tabIndex={0}
            sx={{ cursor: 'pointer', display: 'inline-flex' }}
          >
            <Typography variant="caption" color="error" component="span">
              Out of sync
            </Typography>
          </Box>
        ) : (
          <Box ref={setAnchorRef} display="inline-flex">
            <Chip
              size="small"
              color="warning"
              label="Out of sync"
              onClick={openDrift}
            />
          </Box>
        )}
      </Tooltip>
      <SFDCDriftResolutionPopover
        anchorEl={anchorEl}
        open={isDriftOpen && anchorEl !== null}
        onClose={closeDrift}
        entityType="location"
        entityKey={props.location.key}
        entityDisplayName={props.location.name}
        externalUrl={driftIntegration.link}
        externalLabel={driftIntegration.label}
        onResolved={props.onResolved}
      />
    </>
  );
}
